import React, { ChangeEventHandler, FocusEventHandler, type FC } from "react";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { t } from "i18next";
import { CreateProductValues } from "@/app/nutrition/create-product.schema";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { Button } from "../atoms/Button";
import { FormikErrors } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

export type CreateProductFormProps = {
  isLoading?: boolean;
  values: CreateProductValues;
  errors: FormikErrors<CreateProductValues>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLInputElement>;
  onEquivalence: () => void;
  onEquivalenceDelete: (index: number) => void;
  onCart: () => void;
  onCartDelete: (index: number) => void;
};

export const CreateProductForm: FC<CreateProductFormProps> = ({
  isLoading,
  errors,
  values,
  onChange,
  onBlur,
  onEquivalence,
  onEquivalenceDelete,
  onCart,
  onCartDelete,
}) => {
  const { t } = useTranslation();
  return (
    <form action="">
      <div className="mt-6 mx-2">
        <div className="space-y-12">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("contexts.nutrition.product_name")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("contexts.shared.spanish")}
                  type="text"
                  name="product.productEs"
                  value={values.product.productEs}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.product?.productEs}
                  textError={errors?.product?.productEs}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("contexts.shared.english")}
                  type="text"
                  name="product.productEn"
                  value={values.product.productEn}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.product?.productEn}
                  textError={errors?.product?.productEn}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("contexts.shared.portuguese")}
                  type="text"
                  name="product.productPt"
                  value={values.product.productPt}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.product?.productPt}
                  textError={errors?.product?.productPt}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("contexts.nutrition.nutritional_information")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("contexts.nutrition.calories")}
                  type="text"
                  name="nutritionalInformation.calories"
                  value={values.nutritionalInformation.calories}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.nutritionalInformation?.calories}
                  textError={errors?.nutritionalInformation?.calories}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-3">
                <InputWithCornerHint
                  label={t("contexts.nutrition.proteins")}
                  type="text"
                  name="nutritionalInformation.proteins"
                  value={values.nutritionalInformation.proteins}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.nutritionalInformation?.proteins}
                  textError={errors?.nutritionalInformation?.proteins}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-3">
                <InputWithCornerHint
                  label={t("contexts.nutrition.carbohydrates")}
                  type="text"
                  name="nutritionalInformation.carbohydrates"
                  value={values.nutritionalInformation.carbohydrates}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.nutritionalInformation?.carbohydrates}
                  textError={errors?.nutritionalInformation?.carbohydrates}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-3">
                <InputWithCornerHint
                  label={t("contexts.nutrition.fibers")}
                  type="text"
                  name="nutritionalInformation.fibers"
                  value={values.nutritionalInformation.fibers}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.nutritionalInformation?.fibers}
                  textError={errors?.nutritionalInformation?.fibers}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-3">
                <InputWithCornerHint
                  label={t("contexts.nutrition.fats")}
                  type="text"
                  name="nutritionalInformation.fats"
                  value={values.nutritionalInformation.fats}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors?.nutritionalInformation?.fats}
                  textError={errors?.nutritionalInformation?.fats}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("contexts.nutrition.equivalences")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-8 md:col-span-2">
              <div className="col-span-full flex flex-col gap-4">
                {values.equivalences.map((equivalence, index) => (
                  <div className="grid grid-cols-8 gap-x-6 relative">
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {equivalence.quantity}
                    </p>
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {equivalence.portionEs}
                    </p>
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {equivalence.portionEn}
                    </p>
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {equivalence.portionPt}
                    </p>
                    <p className="flex items-center justify-center absolute right-[100%] h-full aspect-square">
                      <a
                        href="#"
                        className="text-red-600 hover:text-red-900"
                        onClick={() => onEquivalenceDelete(index)}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </a>
                    </p>
                  </div>
                ))}
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.quantity")}
                  type="text"
                  name="temp.equivalences.quantity"
                  value={values.temp.equivalences.quantity}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.spanish")}
                  type="text"
                  name="temp.equivalences.portionEs"
                  value={values.temp.equivalences.portionEs}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.english")}
                  type="text"
                  name="temp.equivalences.portionEn"
                  value={values.temp.equivalences.portionEn}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.portuguese")}
                  type="text"
                  name="temp.equivalences.portionPt"
                  value={values.temp.equivalences.portionPt}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-6">
                <p className="text-sm text-red-500">{`${errors.equivalences ?? ''}`}</p>
              </div>
              <div className="col-span-2 flex">
                <Button
                  text={t("contexts.shared.add")}
                  className="mt-auto flex-1 flex justify-center"
                  onClick={onEquivalence}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("contexts.nutrition.shopping_cart")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl gap-x-6 gap-y-8 grid-cols-6 md:col-span-2">
              <div className="col-span-full flex flex-col gap-4">
                {values.carts.map((cart, index) => (
                  <div className="grid grid-cols-6 gap-x-6 relative">
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {cart.es}
                    </p>
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {cart.en}
                    </p>
                    <p className="flex items-center col-span-2 bg-gray-200 p-2 rounded-md border-solid border-gray-300 border">
                      {cart.pt}
                    </p>
                    <p className="col-span-2 flex items-center justify-center absolute right-[100%] h-full aspect-square">
                      <a
                        href="#"
                        className="text-red-600 hover:text-red-900"
                        onClick={() => onCartDelete(index)}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </a>
                    </p>
                  </div>
                ))}
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.spanish")}
                  type="text"
                  name="temp.carts.es"
                  value={values.temp.carts.es}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.english")}
                  type="text"
                  name="temp.carts.en"
                  value={values.temp.carts.en}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-2">
                <InputWithCornerHint
                  label={t("contexts.shared.portuguese")}
                  type="text"
                  name="temp.carts.pt"
                  value={values.temp.carts.pt}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-4">
                <p className="text-sm text-red-500">{`${errors.carts ?? ''}`}</p>
              </div>
              <div className="col-span-2 flex align-baseline">
                <Button
                  text={t("contexts.shared.add")}
                  className="mt-auto flex-1 flex justify-center"
                  onClick={onCart}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
